'use client';

import { ArrowDownNarrowWide, ArrowDownUp, ArrowDownWideNarrow } from 'lucide-react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import FilterSortContainer from '@//containers/FilterSort';
import type { ElasticSearchResponseDocumentListSortOption } from '@//ElasticSearch';

const DEFAULT_SORT = 'itemSortDesc';

const extractSortValue = (query: string) => {
  const params = new URLSearchParams(query);
  const sort = params.get('sort') || '';
  return sort === DEFAULT_SORT ? '' : sort;
};

const getSortIcon = (sortValue: string | undefined) => {
  switch (sortValue) {
    case '':
    case 'priceDefaultDesc':
    case 'modelYearDesc':
    case 'mileageDesc':
      return <ArrowDownWideNarrow aria-hidden="true" />;
    case 'priceDefaultAsc':
    case 'modelYearAsc':
    case 'mileageAsc':
      return <ArrowDownNarrowWide aria-hidden="true" />;
    default:
      return <ArrowDownUp aria-hidden="true" />;
  }
};

interface CampaignSortProps {
  sortOptions?: ElasticSearchResponseDocumentListSortOption[];
}

const CampaignSort = ({ sortOptions = [] }: CampaignSortProps) => {
  const t = useTranslations('pages.search.sort');
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const currentSort = searchParams.get('sort') || '';

  const handleSortChange = useCallback(
    (value: string) => {
      const nextParams = new URLSearchParams(searchParams);
      if (value) {
        nextParams.set('sort', value);
      } else {
        nextParams.delete('sort');
      }
      // Reset pagination when changing sort
      nextParams.delete('offset');
      const queryString = nextParams.toString();
      router.push(`${pathname}${queryString ? `?${queryString}` : ''}`, { scroll: false });
    },
    [pathname, router, searchParams],
  );

  if (sortOptions.length === 0) {
    return null;
  }

  return (
    <FilterSortContainer
      sortOptions={sortOptions}
      currentSort={currentSort}
      defaultSort=""
      extractSortValue={extractSortValue}
      onSortChange={handleSortChange}
      getSortIcon={getSortIcon}
      buttonTitle={t('button.title')}
      buttonAriaLabel={t('button.ariaLabel', { currentSort: '{currentSort}' })}
      buttonPrefix={t('button.prefix')}
      buttonUnknown={t('button.unknown')}
      menuAriaLabel={t('menu.ariaLabel')}
      menuOptionTitle={t('menu.optionTitle', { option: '{option}' })}
    />
  );
};

export default CampaignSort;
